<template>
  <div
    v-if="stepData"
    class="step"
  >
    <component
      :is="question.questionComponent"
      v-for="question in questions"
      :key="question.questionId || question.questionComponent"
      product-name="car"
      step-id="cover"
      :question-id="question.questionId"
      v-bind="question.params || {}"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="stepData"
      :show-confirm-modal="showConfirmModal"
      :drivers-backup="driversBackup"
      :additional-driver-state-validity="additionalDriverStateValidity"
      @toast="showToast($event)"
      @input="stepData = { ...stepData, ...$event }"
      @add-driver="$emit('add-driver')"
      @change-driver="$emit('change-driver', $event)"
      @delete-driver="$emit('delete-driver', $event)"
      @backup-additional-drivers="$emit('backup-additional-drivers', $event)"
      @restore-additional-drivers="$emit('restore-additional-drivers', $event)"
      @remove-additional-drivers="$emit('remove-additional-drivers')"
      @additional-drivers-status-check="hasAdditionalDrivers = $event"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { cloneDeep } from 'lodash';
import useExperimentMinimiseHelptext from '@/composables/useExperimentMinimiseHelptext';
import {
  inject,
  computed,
} from '@vue/composition-api';

export default {
  name: 'form-car-step-3',
  props: {
    formObserver: {
      type: Object,
      required: true,
    },
    form: {
      type: Object,
      required: true,
    },
    driversBackup: {
      type: Array,
      default: null,
    },
    productName: {
      type: String,
      default: '',
    },
    additionalDriverStateValidity: {
      type: Array,
      default: null,
    },
  },
  inject: ['constants', 'productName'],
  data () {
    return {
      stepData: null,
      hasAdditionalDrivers: this.form.drivers.length > 1,
    };
  },
  computed: {
    step () { return this.form.cover; },
    dateOfInsuranceStartHelpText () {
      const helpText = 'Prices from some insurance providers will often be lower further from your renewal date, compared to the prices in the few days leading up to your renewal. It’s good to make sure there are no gaps in your cover, so you’re always insured.</p>';
      const helpTextTitle = '<p><strong>Good to know</strong><br>';
      return {
        value: this.isExperimentMinimiseHelptext ? helpText : helpTextTitle + helpText,
        collapsible: this.isExperimentMinimiseHelptext,
        openerText: 'Renew Early to Lower Insurance Costs',
      };
    },
    insuranceEverDeclinedHelpText () {
      const helpText = 'This is usually for reasons such as non-disclosure of material or important facts, criminal convictions or misrepresentation by the proposer or any other person named on the policy etc.';
      const helpTextTitle = '<strong>Why are insurance policies cancelled or declined?</strong><br>';
      return {
        value: this.isExperimentMinimiseHelptext ? helpText : helpTextTitle + helpText,
        collapsible: this.isExperimentMinimiseHelptext,
        openerText: 'Reasons for Policy Cancellation or Declines',
      };
    },
    questions () {
      const questions = [
        {
          questionComponent: 'question-additional-drivers',
          questionId: 'additional-drivers',
          params: {
            title: 'Would you like to add any additional drivers to this policy?',
            helpText: 'Some insurance providers on our panel may provide cheaper prices for customers who have additional drivers on the policy, as they believe there is less risk with a shared car.',
          },
        },
        {
          questionComponent: 'question-date-of-insurance-start',
          questionId: 'date-of-insurance-start',
          params: {
            helpText: this.dateOfInsuranceStartHelpText,
          },
        },
        {
          questionComponent: 'question-base-radio-list',
          questionId: 'type-of-cover',
          params: {
            customFields: {
              field: 'typeOfCover',
            },
            optionsConfig: {
              options: {
                1: {
                  text: 'Comprehensive',
                  helpText: 'Cover provided if your car\'s damaged, stolen or written off. You’ll be covered for claims made against you by other people too.',
                },
                2: {
                  text: 'Third Party, Fire & Theft',
                  helpText: 'If your car\'s stolen, or damaged by fire, you can claim. You’re also covered for claims made against you by other people.',
                },
                3: {
                  text: 'Third Party Only',
                  helpText: 'Cover for claims made against you by other people but there’s no cover for you or your car.',
                },
              },
            },
            title: 'What type of cover would you like?',
            validationRules: {
              field: {
                required: true,
              },
            },
            defaultValues: {
              field: 1,
            },
          },
        },
        {
          questionComponent: 'question-base-dropdown',
          questionId: 'voluntary-excess',
          params: {
            customFields: {
              field: 'voluntaryExcess',
            },
            optionsConfig: {
              options: [
                { value: 1000, text: '£ 1000' },
                { value: 950, text: '£ 950' },
                { value: 900, text: '£ 900' },
                { value: 850, text: '£ 850' },
                { value: 800, text: '£ 800' },
                { value: 750, text: '£ 750' },
                { value: 700, text: '£ 700' },
                { value: 650, text: '£ 650' },
                { value: 600, text: '£ 600' },
                { value: 550, text: '£ 550' },
                { value: 500, text: '£ 500' },
                { value: 450, text: '£ 450' },
                { value: 400, text: '£ 400' },
                { value: 350, text: '£ 350' },
                { value: 300, text: '£ 300' },
                { value: 250, text: '£ 250' },
                { value: 200, text: '£ 200' },
                { value: 150, text: '£ 150' },
                { value: 100, text: '£ 100' },
                { value: 50, text: '£ 50' },
                { value: 0, text: 'None' },
              ],
            },
            title: 'What voluntary excess would you like on this policy?',
            helpText: `
              <p>
                Select the amount which you will pay if you make a claim. A higher voluntary excess normally reduces your premium. This will be <b>in addition</b> to any compulsory excesses added onto the policy.
              </p>
            `,
            validationRules: {
              field: {
                required: true,
              },
            },
            defaultValues: {
              field: 500,
            },
          },
        },
        {
          questionComponent: 'question-no-claim-bonus',
          questionId: 'no-claims-bonus',
          params: {
            helpText: {
              title: `Your most recent insurance renewal notice should indicate how many years of NCD (no claims discount) you have earned. NCD is earned for each year you have held ${this.productName} insurance in your own name without making a claim.`,
              value: `If your NCD was earned on a company ${this.productName}, abroad (not in the UK) or if its more than 2 years since you last used it, please check this will be valid with your chosen insurer before buying the policy. You can't use a NCD on more than one ${this.productName === 'car' ? this.productName : 'vehicle'} at a time.`,
              collapsible: true,
              openerText: this.isExperimentMinimiseHelptext ? 'Ensure Your NCD Is Valid' : 'Show More',
              closerText: this.isExperimentMinimiseHelptext ? 'Ensure Your NCD Is Valid' : 'Hide',
            },
          },
        },
        {
          questionComponent: 'question-another-insurance',
          questionId: 'another-insurance',
          params: {
            defaultValues: {
              anotherInsurance: false,
            },
          },
        },
        {
          questionComponent: 'question-type-experience',
          questionId: 'type-experience',
        },
        {
          questionComponent: 'question-free-driving',
          questionId: 'free-driving',
        },
        {
          questionComponent: 'question-protect-bonus',
          questionId: 'protect-bonus',
        },
        {
          questionComponent: 'question-use-of-other-vehicles',
          questionId: 'use-of-other-vehicles',
        },
        {
          questionComponent: 'question-base-dropdown',
          questionId: 'no-claims-bonus-other-vehicle',
          params: {
            customFields: {
              field: 'noClaimsBonusOtherVehicle',
            },
            optionsConfig: {
              placeholder: 'Please select years',
              options: [
                { value: 0, text: 'No NCD' },
                { value: 1, text: '1 Year' },
                { value: 2, text: '2 Years' },
                { value: 3, text: '3 Years' },
                { value: 4, text: '4 Years' },
                { value: 5, text: '5 Years' },
                { value: 6, text: '6 Years' },
                { value: 7, text: '7 Years' },
                { value: 8, text: '8 Years' },
                { value: 9, text: '9 Years' },
                { value: 10, text: '10 Years' },
                { value: 11, text: '11 Years' },
                { value: 12, text: '12 Years' },
                { value: 13, text: '13 Years' },
                { value: 14, text: '14 Years' },
                { value: 15, text: '15 Years' },
                { value: 16, text: '16 Years' },
                { value: 17, text: '17 Years' },
                { value: 18, text: '18 Years' },
                { value: 19, text: '19 Years' },
                { value: 20, text: '20 Years or more' },
              ],
            },
            title: 'How many years of no-claims discount do you have for the other vehicle?',
            helpText: {
              value: '<p>No claims discount earned with an additional vehicle could potentially give you some policy discount.</p><p><strong>Note:</strong> Some insurers may require evidence before applying a discount.</p>',
              priceSensitive: true,
            },
            validationRules: {
              field: {
                required: true,
              },
            },
            validationErrors: {
              field: {
                required: 'Please make your choice.',
              },
            },
            showRules: {
              useOfOtherVehicles: {
                isAny: {
                  other: ['O', 'M', 'V', 'S'],
                },
                required: true,
              },
            },
          },
        },
        {
          questionComponent: 'question-main-user-of-vehicle',
          questionId: 'main-user-of-vehicle',
          params: {
            defaultValues: {
              mainUserOfVehicle: 1,
            },
          },
        },
        {
          questionComponent: 'question-number-of-other-vehicles',
          questionId: 'number-of-other-vehicles',
          params: {
            helpText: {
              priceSensitive: true,
              value: '<p>Some of our insurance providers may give a discount if you drive more than one vehicle.</p><p>This is the number of cars in the household not including this one. If you are purchasing this car to replace another car neither should be included.</p>',
            },
          },
        },
        {
          questionComponent: this.isExperimentMinimiseHelptext ? 'question-base-radio-boolean' : 'question-pay-insurance-monthly',
          questionId: 'pay-insurance-monthly',
          params: {
            customFields: {
              field: 'payInsuranceMonthly',
            },
            title: 'Do you normally pay for your insurance monthly?',
            helpText: {
              value: '<p>Paying via monthly instalments allows you to spread the cost over the year, but you may end up paying more in total.</p><p>Paying annually may mean you pay less overall for the policy by avoiding any credit fees.</p>',
              collapsible: true,
              openerText: 'Monthly vs. Annual Payments',
            },
            validationRules: {
              field: {
                required: true,
              },
            },
          },
        },
        {
          questionComponent: 'question-base-radio-boolean',
          questionId: 'insurance-ever-declined',
          params: {
            customFields: {
              field: 'insuranceEverDeclined',
            },
            title: 'Have you or any drivers ever had insurance declined, cancelled, voided or special terms imposed?',
            helpText: this.insuranceEverDeclinedHelpText,
            validationRules: {
              field: {
                required: true,
              },
            },
            defaultValues: {
              field: false,
            },
          },
        },
        {
          questionComponent: 'question-password',
          questionId: 'user-data',
        },
        {
          questionComponent: 'question-hear-about-us',
          questionId: 'hear-about-us',
          params: {
            customFields: {
              field: 'hearAboutUs',
            },
            optionsConfig: {
              options: {
                3: 'Existing Customer',
                4: 'Friend or Family',
                17: 'Other Website',
                7: 'Search Engine',
                8: 'Television - UTV',
                26: 'Television - Sky TV',
                27: 'Television - Channel 5',
                19: 'Television - Channel 4',
                28: 'Cinema',
                18: 'Facebook',
                25: 'Twitter',
                29: 'Snapchat',
                22: 'Youtube',
                32: 'Spotify',
                6: 'Radio - Cool FM',
                20: 'Radio - U105',
                21: 'Radio - Q Radio',
                31: 'Radio - Downtown',
                30: 'Golf Club Advertising',
                16: 'Taxi Advert',
                14: 'Billboard',
                15: 'Bus Advert',
                5: 'Newspaper',
              },
            },
            title: 'Where did you hear about us?',
            validationRules: {
              field: {
                required: true,
              },
            },
            validationErrors: {
              field: {
                required: 'Please select an option from this list.',
              },
            },
          },
        },
        ...this.questionsCommunicationOptIn,
        ...this.questionUserKeepSignIn,
        {
          questionComponent: 'question-terms-and-conditions',
          questionId: 'terms-and-conditions',
        },
      ];

      if (!this.isCrossSellBreakdown) return questions;
      return questions.toSpliced(questions.length - 2, 0, this.crossSellBreakdownQuestion);
    },
  },
  watch: {
    stepData: {
      handler (val) {
        this.$emit('step-data', val);
      },
      immediate: true,
    },
    'formObserver.valid': {
      immediate: true,
      handler (val) {
        if (val) this.$emit('valid');
      },
    },
  },
  async created () {
    this.$emit('tracking', 'cover');
    await this.initStep();
  },
  methods: {
    ...mapActions(['showToast', 'showConfirmModal']),
    async initStep () {
      this.stepData = cloneDeep(this.form.cover);
      this.formObserver.reset();
    },
  },
  setup () {
    // COMPOSABLES
    const { isExperimentMinimiseHelptext } = useExperimentMinimiseHelptext();
    const {
      isQZCarCommunicationOptIn,
      isCrossSellBreakdownCoverFromCar,
    } = inject('abTastyControllerUI');
    const { isQuotezonePartner } = inject('helpers');
    const { partner } = inject('config');

    const isCrossSellBreakdown = computed(() => {
      return isQuotezonePartner(partner) && isCrossSellBreakdownCoverFromCar.value;
    });

    const crossSellBreakdownQuestion = {
      questionComponent: 'question-cheap-breakdown-cover',
      questionId: 'cheap-breakdown-cover',
      params: {
        customFields: {
          field: 'cheapBreakdownCover',
        },
        title: 'Cheap Breakdown Cover',
        validationRules: {
          field: {
            required: true,
          },
        },
        validationErrors: {
          field: {
            required: 'Please select an option.',
          },
        },
      },
    };

    const testUserKeepSignIn = [];
    const userKeepSignIn = [{
      questionComponent: 'question-user-keep-sign-in',
      questionId: 'user-keep-sign-in',
      params: {
        defaultValues: {
          userKeepSignIn: true,
        },
      },
    }];
    const testCommunicationOptIn = [
      {
        questionComponent: 'question-direct-marketing-options',
        questionId: 'direct-marketing-options',
      },
      {
        questionComponent: 'question-outbound',
        questionId: 'outbound',
      },
    ];
    const communicationOptIn = [
      {
        questionComponent: 'question-outbound',
        questionId: 'outbound',
      },
      {
        questionComponent: 'question-direct-marketing-options',
        questionId: 'direct-marketing-options',
      },
    ];
    const questionsCommunicationOptIn = computed(() => {
      return isQuotezonePartner(partner) && isQZCarCommunicationOptIn.value ? testCommunicationOptIn : communicationOptIn;
    });
    const questionUserKeepSignIn = computed(() => {
      return isQuotezonePartner(partner) && isQZCarCommunicationOptIn.value ? testUserKeepSignIn : userKeepSignIn;
    });

    return {
      isCrossSellBreakdown,
      crossSellBreakdownQuestion,
      isExperimentMinimiseHelptext,
      isQZCarCommunicationOptIn,
      questionsCommunicationOptIn,
      questionUserKeepSignIn,
    };
  },
};
</script>
